import { useState } from "react";
import styled from "styled-components";

import { entryColors, entryFont } from "@entry/tokens";
import { Translations, i18n } from "@i18n";

import { InfoIcon } from "./InfoIcon";
import { ReserveRateInfoModal } from "./ReserveRateInfoModal";
import { AccountTypeCardContent } from "./types";

type SuggestedAccountTypeCardProps = AccountTypeCardContent & {
  selected: boolean;
  onSelect: () => void;
  showExclusive?: boolean;
  showBrandIcon?: boolean;
};

const Card = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  border-radius: 14px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid
    ${({ $selected }) => ($selected ? "rgba(205, 128, 84, 0.8)" : "rgba(255, 255, 255, 0.12)")};
  background: ${({ $selected }) =>
    $selected
      ? "linear-gradient(135deg, rgba(205, 128, 84, 0.18), rgba(143, 82, 53, 0.12))"
      : entryColors.glassFill};
  box-shadow: ${({ $selected }) => ($selected ? "0 0 32px rgba(205, 128, 84, 0.3)" : "none")};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  transition:
    background 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? "linear-gradient(135deg, rgba(205, 128, 84, 0.22), rgba(143, 82, 53, 0.16))"
        : entryColors.glassFillStrong};
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 2px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(205, 128, 84, 0.25);
  border: 1px solid rgba(205, 128, 84, 0.4);
  font-family: ${entryFont.body};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${entryColors.highlight};
`;

const ExclusiveBadge = styled(Badge)`
  background: rgba(61, 106, 82, 0.25);
  border-color: rgba(61, 106, 82, 0.4);
  color: #9dcbbb;
`;

const Indicator = styled.span<{ $selected: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid
    ${({ $selected }) => ($selected ? entryColors.terracotta : "rgba(255, 255, 255, 0.3)")};
  background: ${({ $selected }) => ($selected ? entryColors.terracotta : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InnerCard = styled.div`
  margin: 0 18px;
  padding: 18px;
  border-radius: 10px;
  background: rgba(244, 241, 242, 0.06);
`;

const Title = styled.div`
  font-family: ${entryFont.title};
  font-size: 22px;
  font-weight: 600;
  color: ${entryColors.textPrimary};
`;

const Description = styled.div`
  margin-top: 6px;
  font-family: ${entryFont.body};
  font-size: 14px;
  line-height: 1.45;
  color: ${entryColors.textSecondary};
`;

const InfoRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 18px 18px;
`;

const InfoBox = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-family: ${entryFont.body};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const StrengthLabel = styled(InfoLabel)`
  color: #9dcbbb;
`;

const LimitationLabel = styled(InfoLabel)`
  color: ${entryColors.highlight};
`;

const InfoText = styled.div`
  font-family: ${entryFont.body};
  font-size: 13px;
  line-height: 1.4;
  color: ${entryColors.textSecondary};
`;

const TeyBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 4px 8px 4px 12px;
  border-radius: 999px;
  background: rgba(61, 106, 82, 0.25);
  border: 1px solid rgba(61, 106, 82, 0.4);
  font-family: ${entryFont.body};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #9dcbbb;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

const FMShape = () => (
  <svg
    width="16"
    height="8"
    viewBox="0 0 16 8"
    fill="none"
    aria-hidden="true"
    style={{ marginBottom: 2, flexShrink: 0 }}
  >
    <path
      d="M0 7.6406C-3.86259e-07 3.4216 3.58172 0.00142106 8 0.00142069C12.4183 0.00142032 16 3.4216 16 7.6406L0 7.6406Z"
      fill={entryColors.highlight}
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" aria-hidden="true">
    <path
      d="M1 5.2L4.2 8.4L11 1.6"
      stroke="#FFFFFF"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SuggestedAccountTypeCard = ({
  selected,
  onSelect,
  title,
  description,
  strength,
  limitation,
  showExclusive,
  teyBanner,
  showBrandIcon,
}: SuggestedAccountTypeCardProps) => {
  const [isRateInfoVisible, setIsRateInfoVisible] = useState(false);

  return (
    <>
      <Card
        type="button"
        role="radio"
        aria-checked={selected}
        $selected={selected}
        onClick={onSelect}
      >
        <Header>
          <Badge>{i18n.t(Translations.AccountTypeRouteSuggestedBadge)}</Badge>
          <Indicator $selected={selected}>{selected ? <CheckIcon /> : null}</Indicator>
        </Header>

        <InnerCard>
          <TitleRow>
            <Title>{title}</Title>
            {(showExclusive || showBrandIcon) && <FMShape />}
          </TitleRow>
          <Description>{description}</Description>
          {showExclusive && (
            <ExclusiveBadge style={{ marginTop: 10 }}>
              {i18n.t(Translations.AccountTypeRouteExclusiveLabel)}
            </ExclusiveBadge>
          )}
          {teyBanner && (
            <TeyBanner>
              {teyBanner}
              <InfoIcon size={14} color="#9dcbbb" onClick={() => setIsRateInfoVisible(true)} />
            </TeyBanner>
          )}
        </InnerCard>

        <InfoRow>
          <InfoBox>
            <StrengthLabel>{i18n.t(Translations.AccountTypeRouteStrengthLabel)}</StrengthLabel>
            <InfoText>{strength}</InfoText>
          </InfoBox>
          <InfoBox>
            <LimitationLabel>{i18n.t(Translations.AccountTypeRouteLimitationLabel)}</LimitationLabel>
            <InfoText>{limitation}</InfoText>
          </InfoBox>
        </InfoRow>
      </Card>

      {teyBanner && (
        <ReserveRateInfoModal
          show={isRateInfoVisible}
          onClose={() => setIsRateInfoVisible(false)}
        />
      )}
    </>
  );
};
